package com.castly.admin.ui.podcasts

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.admin.ui.components.LabelSelector
import com.castly.shared.dto.ConvertToAudiobookRequest
import com.castly.shared.dto.ConversionResultDto
import com.castly.shared.dto.LabelDto
import com.castly.shared.dto.PodcastDto
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun ConvertToAudiobookDialog(
    isOpen: Boolean,
    podcast: PodcastDto,
    apiClient: ApiClient,
    labels: List<LabelDto> = emptyList(),
    userLabelId: Int? = null,
    isLabelEditor: Boolean = false,
    onConverted: (ConversionResultDto) -> Unit,
    onCancel: () -> Unit
) {
    if (!isOpen) return

    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()

    var isbn by remember { mutableStateOf("") }
    var authorName by remember { mutableStateOf("") }
    var readerName by remember { mutableStateOf("") }
    var titleOverride by remember { mutableStateOf("") }
    var descriptionOverride by remember { mutableStateOf("") }

    var selectedLabelId by remember { mutableStateOf<Int?>(if (isLabelEditor) userLabelId else null) }
    var isConverting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    // Overlay
    Div({
        onClick { if (!isConverting) onCancel() }
        style {
            property("position", "fixed")
            property("top", "0")
            property("left", "0")
            width(100.vw)
            height(100.vh)
            backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "2000")
        }
    }) {
        // Dialog
        Div({
            onClick { it.stopPropagation() }
            style {
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                padding(Spacing.xxl)
                border(1.px, LineStyle.Solid, Color(colors.border))
                property("box-shadow", "0 4px 6px rgba(0,0,0,0.1)")
                maxWidth(500.px)
                width(90.percent)
                maxHeight(80.vh)
                property("overflow-y", "auto")
                themeTransition()
            }
        }) {
            H3({
                style {
                    margin(0.px)
                    marginBottom(Spacing.md)
                    fontSize(Typography.lg)
                    fontWeight("600")
                    color(Color(colors.text))
                }
            }) { Text("Convert to Audiobook") }

            P({
                style {
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                    marginBottom(Spacing.lg)
                    margin(0.px, 0.px, Spacing.lg, 0.px)
                }
            }) {
                Text("Convert \"${podcast.title}\" to an audiobook. Episodes will become chapters and the podcast will be soft-deleted.")
            }

            // Form fields
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(Spacing.md)
                }
            }) {
                FormField("Title Override", titleOverride, "Leave empty to use podcast title") { titleOverride = it }
                FormField("Description Override", descriptionOverride, "Leave empty to use podcast description") { descriptionOverride = it }
                FormField("ISBN", isbn, "Optional ISBN") { isbn = it }
                FormField("Author Name", authorName, "Optional author name") { authorName = it }
                FormField("Reader Name", readerName, "Optional reader/narrator name") { readerName = it }

                // Label selector
                if (labels.isNotEmpty()) {
                    Div({ style { marginTop(Spacing.md) } }) {
                        LabelSelector(
                            labels = labels,
                            selectedLabelId = selectedLabelId,
                            onLabelSelected = { selectedLabelId = it },
                            readOnly = isLabelEditor,
                            userLabelId = userLabelId
                        )
                    }
                }
            }

            error?.let {
                Div({
                    style {
                        marginTop(Spacing.md)
                        padding(Spacing.sm)
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        borderRadius(BorderRadius.sm)
                        fontSize(Typography.sm)
                        color(Color(colors.danger))
                    }
                }) { Text(it) }
            }

            // Buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(Spacing.sm)
                    marginTop(Spacing.lg)
                }
            }) {
                Button({
                    onClick { onCancel() }
                    if (isConverting) disabled()
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        backgroundColor(Color(colors.surfaceHover))
                        color(Color(colors.text))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        fontSize(Typography.base)
                        cursor("pointer")
                    }
                }) { Text("Cancel") }

                Button({
                    onClick {
                        scope.launch {
                            isConverting = true
                            error = null
                            try {
                                val result = apiClient.convertPodcastToAudiobook(
                                    podcastId = podcast.id,
                                    request = ConvertToAudiobookRequest(
                                        isbn = isbn.takeIf { it.isNotBlank() },
                                        authorName = authorName.takeIf { it.isNotBlank() },
                                        readerName = readerName.takeIf { it.isNotBlank() },
                                        title = titleOverride.takeIf { it.isNotBlank() },
                                        description = descriptionOverride.takeIf { it.isNotBlank() },
                                        labelId = selectedLabelId
                                    )
                                )
                                onConverted(result)
                            } catch (e: Exception) {
                                error = e.message ?: "Conversion failed"
                            } finally {
                                isConverting = false
                            }
                        }
                    }
                    if (isConverting) disabled()
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        backgroundColor(Color(if (isConverting) colors.textMuted else colors.primary))
                        color(Color("#ffffff"))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        fontSize(Typography.base)
                        fontWeight("600")
                        cursor(if (isConverting) "not-allowed" else "pointer")
                    }
                }) { Text(if (isConverting) "Converting..." else "Convert") }
            }
        }
    }
}

@Composable
private fun FormField(
    label: String,
    value: String,
    placeholder: String,
    onValueChange: (String) -> Unit
) {
    val colors = AppTheme.colors
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.xs)
        }
    }) {
        Label(attrs = {
            style {
                fontSize(Typography.sm)
                fontWeight("600")
                color(Color(colors.textMuted))
            }
        }) { Text(label) }
        Input(InputType.Text) {
            value(value)
            onInput { onValueChange(it.value) }
            placeholder(placeholder)
            style {
                width(100.percent)
                padding(Spacing.sm, Spacing.md)
                fontSize(Typography.base)
                border(1.px, LineStyle.Solid, Color(colors.border))
                borderRadius(BorderRadius.md)
                backgroundColor(Color(colors.surface))
                color(Color(colors.text))
                property("box-sizing", "border-box")
                themeTransition()
            }
        }
    }
}
